import React, { useEffect, useState } from "react";
import { api, getReadableError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PHONE_OPERATOR_LABELS,
  PHONE_OPERATOR_OPTIONS,
  COMMON_COUNTRIES,
} from "@/lib/labels";

const EMPTY = {
  name: "",
  phone: "",
  phone_operator: "",
  phone_payment_day: "",
  birth_country: "",
  email: "",
  address: "",
  notes: "",
};

export default function ClientFormDialog({ open, onOpenChange, onSaved, client }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(
        client
          ? {
              name: client.name || "",
              phone: client.phone || "",
              phone_operator: client.phone_operator || "",
              phone_payment_day:
                client.phone_payment_day == null ? "" : String(client.phone_payment_day),
              birth_country: client.birth_country || "",
              email: client.email || "",
              address: client.address || "",
              notes: client.notes || "",
            }
          : EMPTY
      );
    }
  }, [open, client]);

  const isEdit = Boolean(client?.id);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("El nombre es requerido");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.phone_payment_day === "") {
        payload.phone_payment_day = null;
      } else {
        const n = parseInt(payload.phone_payment_day, 10);
        if (Number.isNaN(n) || n < 1 || n > 31) {
          toast.error("El día de pago debe ser entre 1 y 31");
          setSaving(false);
          return;
        }
        payload.phone_payment_day = n;
      }
      if (payload.phone_operator === "") payload.phone_operator = "";

      let data;
      if (isEdit) {
        const r = await api.patch(`/clients/${client.id}`, payload);
        data = r.data;
      } else {
        const r = await api.post("/clients", payload);
        data = r.data;
      }
      onSaved && onSaved(data);
    } catch (err) {
      toast.error(getReadableError(err));
    } finally {
      setSaving(false);
    }
  };

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-none border-slate-900 max-w-xl max-h-[92vh] overflow-y-auto"
        data-testid="client-form-dialog"
      >
        <DialogHeader>
          <div className="label-eyebrow text-[#6B21A8]">
            {isEdit ? "·Editar cliente" : "·Nuevo cliente"}
          </div>
          <DialogTitle className="font-display text-2xl tracking-tight">
            {isEdit ? form.name || "Cliente" : "Crear cliente"}
          </DialogTitle>
          <DialogDescription>
            Solo el nombre es obligatorio. Los demás campos se pueden completar luego.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <Field label="Nombre" required>
            <Input
              value={form.name}
              onChange={update("name")}
              required
              className="rounded-none h-11 border-slate-300 focus-visible:ring-2 focus-visible:ring-[#6B21A8] focus-visible:ring-offset-0"
              placeholder="Juan Pérez"
              autoFocus
              data-testid="client-name-input"
            />
          </Field>

          <Field label="Teléfono">
            <Input
              type="tel"
              inputMode="tel"
              value={form.phone}
              onChange={update("phone")}
              className="rounded-none h-11 border-slate-300 focus-visible:ring-2 focus-visible:ring-[#6B21A8] focus-visible:ring-offset-0 font-mono-tab"
              placeholder="+1 555 123 4567"
              data-testid="client-phone-input"
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Operadora">
              <Select
                value={form.phone_operator || "ninguno"}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, phone_operator: v === "ninguno" ? "" : v }))
                }
              >
                <SelectTrigger
                  className="rounded-none h-11 border-slate-300 focus:ring-2 focus:ring-[#6B21A8] focus:ring-offset-0"
                  data-testid="client-operator-select"
                >
                  <SelectValue placeholder="Selecciona" />
                </SelectTrigger>
                <SelectContent className="rounded-none border-slate-900">
                  {PHONE_OPERATOR_OPTIONS.map((k) => (
                    <SelectItem key={k} value={k} className="rounded-none">
                      {PHONE_OPERATOR_LABELS[k]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Día de pago">
              <Input
                type="number"
                min="1"
                max="31"
                value={form.phone_payment_day}
                onChange={update("phone_payment_day")}
                className="rounded-none h-11 border-slate-300 focus-visible:ring-2 focus-visible:ring-[#6B21A8] focus-visible:ring-offset-0 font-mono-tab"
                placeholder="Ej: 15"
                data-testid="client-payment-day-input"
              />
            </Field>
          </div>

          <Field label="Correo">
            <Input
              type="email"
              value={form.email}
              onChange={update("email")}
              className="rounded-none h-11 border-slate-300 focus-visible:ring-2 focus-visible:ring-[#6B21A8] focus-visible:ring-offset-0"
              placeholder="cliente@email.com"
              data-testid="client-email-input"
            />
          </Field>

          <Field label="País de nacimiento">
            <Input
              value={form.birth_country}
              onChange={update("birth_country")}
              list="country-options"
              className="rounded-none h-11 border-slate-300 focus-visible:ring-2 focus-visible:ring-[#6B21A8] focus-visible:ring-offset-0"
              placeholder="Ej: República Dominicana"
              data-testid="client-birth-country-input"
            />
            <datalist id="country-options">
              {COMMON_COUNTRIES.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </Field>

          <Field label="Dirección">
            <Input
              value={form.address}
              onChange={update("address")}
              className="rounded-none h-11 border-slate-300 focus-visible:ring-2 focus-visible:ring-[#6B21A8] focus-visible:ring-offset-0"
              placeholder="Calle, ciudad, país"
              data-testid="client-address-input"
            />
          </Field>

          <Field label="Notas">
            <Textarea
              value={form.notes}
              onChange={update("notes")}
              className="rounded-none border-slate-300 focus-visible:ring-2 focus-visible:ring-[#6B21A8] focus-visible:ring-offset-0 min-h-[80px]"
              placeholder="Observaciones libres..."
              data-testid="client-notes-input"
            />
          </Field>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-none border-slate-300"
              data-testid="client-form-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="rounded-none bg-[#6B21A8] hover:bg-[#581C87] text-white font-bold uppercase tracking-wider btn-swap"
              data-testid="client-form-submit-button"
            >
              {saving ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear cliente"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="label-eyebrow">
        {label}
        {required ? " *" : ""}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
